import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import InspirationSection from './components/InspirationSection';
import PromptForm from './components/PromptForm';
import JSONPreview from './components/JSONPreview';
import FloatingSquares from './components/FloatingSquares';
import ExamplesSection from './components/ExamplesSection';
import { randomizeAll, randomizeField } from './utils/randomizer';
import './App.css';

function App() {
  const [formData, setFormData] = useState({
    targetModel: 'Google Veo 3.1',
    duration: 8,
    sceneDescription: '',
    composition: '',
    cameraMovement: '',
    visualStyle: '',
    lighting: '',
    environment: '',
    audio: '',
    dialogue: '',
    selectedColorPalette: null,
    customComposition: '',
    customCameraMovement: '',
    customVisualStyle: '',
    customLighting: '',
    customEnvironment: '',
    customAudio: ''
  });

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const updateCustomField = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleRandomizeAll = useCallback(() => {
    const randomData = randomizeAll();
    // Preserve the manually selected model and duration if any
    setFormData(prev => ({
      ...prev,
      ...randomData,
      targetModel: prev.targetModel, // Keep existing model
      duration: prev.duration // Keep existing duration
    }));
  }, []);

  const handleRandomizeField = useCallback((field) => {
    const randomData = randomizeField(field);
    setFormData(prev => ({
      ...prev,
      ...randomData
    }));
  }, []);

  const handleClear = useCallback(() => {
    setFormData(prev => ({
      targetModel: prev.targetModel, // Keep model selection
      duration: prev.duration, // Keep duration selection
      sceneDescription: '',
      composition: '',
      cameraMovement: '',
      visualStyle: '',
      lighting: '',
      environment: '',
      audio: '',
      dialogue: '',
      selectedColorPalette: null,
      customComposition: '',
      customCameraMovement: '',
      customVisualStyle: '',
      customLighting: '',
      customEnvironment: '',
      customAudio: ''
    }));
  }, []);

  return (
    <div className="min-h-screen py-10 px-4 app-container">
      {/* Background with floating squares */}
      <FloatingSquares />
      
      {/* Content with higher z-index */}
      <div className="container mx-auto max-w-6xl relative z-10 content-container">
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 text-center"
        >
          <h1 className="text-3xl md:text-4xl font-bold neon-text mb-2">JSON Prompt Builder</h1>
          <p className="text-white">Create professional JSON prompts for Sora 2, Veo 3.1 & Gen-3</p>
        </motion.header>
        
        <InspirationSection 
          onRandomizeAll={handleRandomizeAll}
          onRandomizeField={handleRandomizeField}
        />
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
          <PromptForm 
            formData={formData}
            updateField={updateField}
            updateCustomField={updateCustomField}
          />
          
          <JSONPreview 
            formData={formData}
            onClear={handleClear}
          />
        </div>
        
        <ExamplesSection />
        
        <footer className="text-center text-gray-500 text-sm py-6 border-t border-gray-800 mt-8">
          <p>© {new Date().getFullYear()} JSON Prompt Builder. Built for the future of AI video.</p>
        </footer>
      </div>
    </div>
  );
}

export default App;